import { MeetingFormats } from "@/constants"
import { DataContext } from "@/contexts"
import TimerSharpIcon from "@mui/icons-material/TimerSharp"
import { Typography } from "@mui/material"
import PropTypes from "prop-types"
import React, { Fragment, useCallback, useContext, useState } from "react"
import useFetchScheduleProgram from "../opening_module/pages/curriculum/hooks/useFetchScheduleProgram"
import ActionItem from "./ActionItem"
import Agenda from "./Agenda"
import Headline from "./Headline"
import { NewAssignmentModal } from "./NewAssigmentModal"
import { SessionAction } from "./SessionAction"
import { NextSessionContainer, SessionDetails, SessionHeader } from "./styles"
import { mapSessionData } from "./utils"

export const UpcomingSession = React.memo(({ upcomingSession, currentUser }) => {
  const { scheduleId } = useContext(DataContext)
  const sessionData = upcomingSession && mapSessionData(upcomingSession)
  const { programLevels, loading: loadingProgram } = useFetchScheduleProgram({ scheduleId: scheduleId })
  const userRole = currentUser?.role
  const [showNewAssignmentModal, setShowNewAssignmentModal] = useState(false)
  const [assignmentId, setUpdatedAssignmentId] = useState(upcomingSession?.assignment?.id)
  const hasLessonAssigned = upcomingSession?.assignment?.id

  const onJoinClassClick = useCallback(
    (e, skipAssignmentModal) => {
      if (!hasLessonAssigned && !skipAssignmentModal && !assignmentId && programLevels.length) {
        setShowNewAssignmentModal(true)
        return
      }

      if (e && e.preventDefault) e.preventDefault()
      if (!sessionData) return

      const { location, address, scheduleId } = sessionData

      if (location === MeetingFormats.Online) {
        window.open(address, "_blank")
      } else if (location === MeetingFormats.LessonSpace && scheduleId) {
        window.open(`/${userRole}/dashboard/classroom/${scheduleId}`, "_blank")
      }
    },
    [hasLessonAssigned, assignmentId, programLevels.length, sessionData, userRole]
  )

  if (!upcomingSession || !sessionData?.assignmentActionItems) {
    return null
  }

  const { canStartNextSession, isOfflineSession } = sessionData

  return (
    <NextSessionContainer>
      <SessionDetails>
        <SessionHeader>
          <TimerSharpIcon sx={{ color: "rgb(240, 35, 14)" }} aria-label="Up next icon" />
          <Typography variant="h6" component="div" color="textPrimary">
            Up next
          </Typography>
        </SessionHeader>
        <Headline {...sessionData} />
        <Agenda {...sessionData} />
        {sessionData.session_type === "lesson" && <ActionItem {...sessionData} />}
      </SessionDetails>
      {!isOfflineSession && (
        <Fragment>
          <SessionAction canStartNextSession={canStartNextSession} onJoinClassClick={onJoinClassClick} />
          <NewAssignmentModal
            show={showNewAssignmentModal}
            scheduleProgramDetails={{
              programLevels,
              loadingProgram,
            }}
            onClose={() => setShowNewAssignmentModal(false)}
            onJoinClassClick={onJoinClassClick}
            setUpdatedAssignmentId={setUpdatedAssignmentId}
            scheduleId={sessionData && sessionData.scheduleId}
            session={sessionData}
          />
        </Fragment>
      )}
    </NextSessionContainer>
  )
})

UpcomingSession.propTypes = {
  upcomingSession: PropTypes.object.isRequired,
}

UpcomingSession.displayName = "UpcomingSession"
